/* Copyright (c) 2020-present CloudIO Inc */

import { DBRow, IoPages } from '@cloudio-saas/datasource-types';
import { useStateXAtomSetter, useStateXAtomValue } from '@cloudio-saas/statex';
import { Typography, Theme, ListItem, ListItemText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import VirtualList from 'components/DataGrid/grid/List';
import Icon, { parseIconText } from 'icons/Icon';
import React, {
  CSSProperties,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAutoSizerContext } from 'hooks/useComponentSize';
import Store from 'store/Store';
import { isFromDB } from 'store/storeTypes';
import { useStoreCurrentRecordIdCallback } from 'store/useAppStore';
import NoDataFound from 'utils/NoDataFound';
import {
  PageView,
  sidebarPagesFilterAtom,
  sidebarPagesFilterResultsSizeAtom,
} from '../../atoms';
import PageActions from './PageActions';
import { CellComponentProps } from '../../../../components/DataGrid/grid/types';
import { TypographyEllipsisProps } from 'utils/commonProps';
import TimeAgo from 'react-timeago';
import { UserProfilePic } from 'components/UserPic/UserPic';

const useStyles = makeStyles((theme: Theme) => ({
  editIcon: {
    display: 'none',
  },
  listItem: {
    display: 'flex',
    paddingLeft: '12px',
    alignItems: 'center',
    '&$active': {
      backgroundColor: theme.palette.action.selected,
    },
    '&:hover,&$active': {
      '& > $editIcon': {
        display: 'flex',
      },
    },
  },
  label: {
    flex: 1,
  },
  icon: {
    marginRight: 12,
    flexShrink: 0,
  },
  userIcon: {
    marginLeft: 6,
    marginBottom: 2,
    alignSelf: 'start',
    flexShrink: 0,
  },
  active: {},
}));

function AppCell({
  value,
  style,
  setView,
  store,
  appUid,
}: {
  appUid: string;
  value: string;
  style?: CSSProperties;
  setView: (_view: PageView) => void;
  store: Store<IoPages>;
}): React.ReactElement {
  const classes = useStyles();
  const record = store.getRecord(value) as DBRow<IoPages>;
  const [active, setActive] = useState(value === store.getCurrentRecordId());

  // use callback to avoid re-render every cell when the current record id changes
  useStoreCurrentRecordIdCallback(store, ({ value: newValue }) => {
    setActive(value === newValue);
  });
  const { iconName, iconType } = parseIconText(record.pageIcon);
  return (
    <ListItem
      className={clsx(classes.listItem, active ? classes.active : null)}
      key={value}
      style={style}
      dense
      data-testid={record.pageUid}
    >
      <Icon
        className={classes.icon}
        iconName={iconName || 'desktop'}
        iconType={iconType}
        height={20}
        width={20}
      />
      <ListItemText
        primaryTypographyProps={TypographyEllipsisProps}
        secondaryTypographyProps={TypographyEllipsisProps}
        primary={
          <>
            <Typography
              variant="body1"
              className={classes.label}
              noWrap
              sx={{ userSelect: 'text' }}
            >
              {`${record.pageName} (${record.pageUid})`}
            </Typography>
          </>
        }
        secondary={
          <>
            <TimeAgo date={record.lastUpdateDate} />
            <UserProfilePic
              userName={record.lastUpdatedBy}
              size={15}
              className={classes.userIcon}
              hideOnlineStatus={true}
            />
          </>
        }
      />
      <PageActions
        key={value}
        className={classes.editIcon}
        setView={setView}
        store={store}
        value={value}
        record={record}
        appUid={appUid}
      />
    </ListItem>
  );
}

const MemoAppCell = memo(AppCell);

const PagesList = ({
  recordsOrder,
  setView,
  store,
  pageUid,
  appUid,
}: {
  appUid: string;
  pageUid: string;
  recordsOrder: string[];
  setView: (_view: PageView) => void;
  store: Store<IoPages>;
}) => {
  let filter = useStateXAtomValue(sidebarPagesFilterAtom);
  const setSize = useStateXAtomSetter(sidebarPagesFilterResultsSizeAtom);
  if (filter) {
    const lcfilter = filter.toLowerCase();
    const records = store.records();
    if (filter.startsWith('pg:')) {
      filter = filter.substring(3);
      recordsOrder = recordsOrder.filter((id) => {
        const row = records[id];
        return row && row.pageName?.toLowerCase() === filter.toLowerCase();
      });
    } else {
      recordsOrder = recordsOrder.filter((id) => {
        const row = records[id];
        return (
          id.toLowerCase().indexOf(lcfilter) !== -1 ||
          (row && row.pageName?.toLowerCase().indexOf(lcfilter) !== -1)
        );
      });
    }
  }
  const size = recordsOrder.length;
  useEffect(() => {
    setSize(size);
  }, [setSize, size]);

  const initRef = useRef(true);

  useEffect(() => {
    const setCurrentRecord = async () => {
      const rec = store.getCurrentRecord();
      if (recordsOrder.length && initRef.current && rec) {
        initRef.current = false;
        const id = store.getCurrentRecordId();
        if (isFromDB(rec)) {
          const r = { ...rec, pageUid };
          const currentId = await store.getPrimaryKey(r);
          if (id !== currentId && recordsOrder.includes(currentId)) {
            store.setCurrentRecordId(currentId);
          }
        }
      }
    };
    setCurrentRecord();
  }, [pageUid, store, recordsOrder]);

  // useCallback to avoid re-render the grid
  const CellComponent = useCallback(
    ({ rowIndex, style }: CellComponentProps) => {
      return (
        <MemoAppCell
          appUid={appUid}
          style={style}
          value={recordsOrder[rowIndex]}
          setView={setView}
          store={store}
        />
      );
    },
    [appUid, recordsOrder, setView, store],
  );
  const { width, height } = useAutoSizerContext();

  if (recordsOrder.length === 0) {
    return (
      <NoDataFound
        message={
          filter ? `No pages found matching "${filter}"!` : 'No pages found!'
        }
      />
    );
  }

  return (
    <VirtualList
      CellComponent={CellComponent}
      rowHeight={52}
      height={height}
      totalRows={recordsOrder.length}
      width={width}
      custom
    />
  );
};

export default memo(PagesList);
